import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { ArrowLeft, Ban, Pencil, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useCurrencyOptions } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import { ErrorBanner, Pagination, StatusBadge } from '../components/catalog'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'
import { Combobox, Select } from '../components/controls'
import {
  CurrencyForm,
  emptyCurrencyForm,
  type CurrencyFormValue,
} from '../components/financeForms'
import type { Currency } from './Currencies'

const TABS = ['summary', 'rates'] as const
type TabValue = (typeof TABS)[number]

const SIDES = ['quoted', 'base', 'any'] as const
type SideValue = (typeof SIDES)[number]

const PAGE_SIZE = 50

export interface RateEntry {
  historyId: number
  currencyHistoryId: number
  currencyBaseId: number
  currencyBase: string
  currencyExchangeId: number
  currencyExchange: string
  /** decimal(28,14): a string, so the 14 decimals survive the trip. */
  rate: string
  date: string
}

interface RatePage {
  entries: RateEntry[]
  total: number
  offset: number
  limit: number
}

/**
 * CurrencyDetail is one currency, split into tabs: its own fields and its
 * exchange-rate history ([CurrencyHub].[History]).
 *
 * The history is READ-ONLY, and visibly so — no add button, no row actions. A
 * rate is not written one row at a time: production inserts a whole snapshot
 * through [CurrencyHub].[CurrencyHistory.Insert], which allocates the batch id
 * and stamps every row of the batch with one timestamp.
 *
 * A currency sits on both ends of a pair, so the tab asks which one you mean:
 * *quoted* is its own series (what one unit of the base buys), *base* is the
 * snapshots published from it, *any* is both. Rates are rendered exactly as
 * stored — trailing zeros trimmed, never re-parsed as a number.
 */
export function CurrencyDetailPage() {
  const { t } = useTranslation('app')
  const { id } = useParams()
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { currencies } = useCurrencyOptions()

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as TabValue | null
  const tab: TabValue = requested && TABS.includes(requested) ? requested : 'summary'

  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<CurrencyFormValue>(emptyCurrencyForm)
  const [side, setSide] = useState<SideValue>('quoted')
  const [counterpart, setCounterpart] = useState('')
  const [offset, setOffset] = useState(0)

  const { data: currency } = useQuery({
    queryKey: ['currencies', id],
    queryFn: async (): Promise<Currency> => {
      const res = await api.get(`/currencies/${id}`)
      return res.data as Currency
    },
  })

  const historyParams = {
    side,
    offset,
    limit: PAGE_SIZE,
    ...(counterpart ? { counterpartId: counterpart } : {}),
  }

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['currency-history', id, historyParams],
    queryFn: async (): Promise<RatePage> => {
      const res = await api.get(`/currencies/${id}/history`, { params: historyParams })
      return res.data as RatePage
    },
  })

  const { data: latest } = useQuery({
    queryKey: ['currency-history-latest', id, side],
    queryFn: async (): Promise<{ entries: RateEntry[] }> => {
      const res = await api.get(`/currencies/${id}/history/latest`, { params: { side } })
      return res.data as { entries: RateEntry[] }
    },
  })

  // Seed the edit form from the loaded currency (and re-seed after a save).
  useEffect(() => {
    if (!currency) return
    setForm({
      currencyId: String(currency.currencyId),
      currency: currency.currency,
      currencyCode: currency.currencyCode,
      currencySymbol: currency.currencySymbol,
      subunit: String(currency.subunit),
      isEnabled: currency.isEnabled,
      isDisplay: currency.isDisplay,
      isCrypto: currency.isCrypto,
    })
  }, [currency])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['currencies'] })
  const onError = (err: unknown) => setError(messageOf(err, t('currencies.errors.save')))

  const save = useMutation({
    mutationFn: () =>
      api.put(`/currencies/${id}`, {
        currency: form.currency.trim(),
        currencyCode: form.currencyCode.trim(),
        currencySymbol: form.currencySymbol.trim(),
        subunit: Number(form.subunit),
        isEnabled: form.isEnabled,
        isDisplay: form.isDisplay,
        isCrypto: form.isCrypto,
        isDisabledByPlatform: currency?.isDisabledByPlatform ?? false,
      }),
    onSuccess: () => {
      setEditing(false)
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleDisabled = useMutation({
    mutationFn: () =>
      currency?.isDisabledByPlatform
        ? api.post(`/currencies/${id}/restore`)
        : api.delete(`/currencies/${id}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  if (!currency) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  }

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')
  const entries = history?.entries ?? []
  const latestEntries = latest?.entries ?? []

  return (
    <div>
      <Link
        to="/currencies"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('currencies.title')}
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">
            {currency.currencyCode} <span className="text-muted-foreground">{currency.currency}</span>
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('currencies.columns.id')} {currency.currencyId}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge disabled={currency.isDisabledByPlatform} />
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleDisabled.isPending}
              onClick={() => toggleDisabled.mutate()}
            >
              {currency.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
              {currency.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
            </Button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="summary">{t('currencies.tabs.summary')}</Tab>
          <Tab value="rates">
            {t('currencies.tabs.rates')}
            <TabCount value={history?.total ?? 0} />
          </Tab>
        </TabsList>

        {/* ---- summary ---- */}
        <TabsPanel value="summary">
          {editing ? (
            <CurrencyForm
              value={form}
              onChange={setForm}
              onSubmit={() => save.mutate()}
              onCancel={() => {
                setEditing(false)
                setError('')
              }}
              isCreate={false}
              pending={save.isPending}
              error={error}
              submitLabel={t('common.save')}
            />
          ) : (
            <>
              <Card className="max-w-3xl">
                <CardContent>
                  <dl className="grid grid-cols-[14rem_1fr] gap-x-6 gap-y-3 text-sm">
                    <SummaryRow label={t('currencies.columns.id')}>
                      <span className="font-mono text-xs">{currency.currencyId}</span>
                    </SummaryRow>
                    <SummaryRow label={t('currencies.columns.name')}>{currency.currency}</SummaryRow>
                    <SummaryRow label={t('currencies.columns.code')}>
                      <span className="font-mono">{currency.currencyCode}</span>
                    </SummaryRow>
                    <SummaryRow label={t('currencies.columns.symbol')}>
                      {currency.currencySymbol}
                    </SummaryRow>
                    <SummaryRow label={t('currencies.columns.subunit')}>
                      <span className="font-mono text-xs">{currency.subunit}</span>
                    </SummaryRow>
                    <SummaryRow label={t('currencies.columns.flags')}>
                      <div className="flex flex-wrap gap-1">
                        {currency.isEnabled && (
                          <Badge variant="secondary">{t('currencies.columns.enabled')}</Badge>
                        )}
                        {currency.isDisplay && (
                          <Badge variant="secondary">{t('currencies.columns.display')}</Badge>
                        )}
                        {currency.isCrypto && (
                          <Badge variant="outline">{t('currencies.columns.crypto')}</Badge>
                        )}
                      </div>
                    </SummaryRow>
                    <SummaryRow label={t('common.columns.created')}>
                      {formatDate(currency.creationDate)}
                    </SummaryRow>
                    <SummaryRow label={t('paymentMethods.summary.lastModified')}>
                      {formatDate(currency.lastModificationDate)}
                    </SummaryRow>
                  </dl>
                </CardContent>
              </Card>

              {canWrite && (
                <Button className="mt-6" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil />
                  {t('common.edit')}
                </Button>
              )}
            </>
          )}
        </TabsPanel>

        {/* ---- rates ---- */}
        <TabsPanel value="rates">
          <p className="mb-4 max-w-3xl text-sm text-muted-foreground">{t('rates.readOnlyHint')}</p>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Select
              value={side}
              onValueChange={(v) => {
                setSide(v as SideValue)
                setOffset(0)
              }}
              items={[
                { value: 'quoted', label: t('rates.sides.quoted') },
                { value: 'base', label: t('rates.sides.base') },
                { value: 'any', label: t('rates.sides.any') },
              ]}
              className="w-56"
            />
            <Combobox
              value={counterpart}
              onValueChange={(v) => {
                setCounterpart(v)
                setOffset(0)
              }}
              items={currencies
                .filter((c) => String(c.id) !== String(currency.currencyId))
                .map((c) => ({ value: String(c.id), label: c.name }))}
              placeholder={t('rates.allCounterparts')}
              className="w-64"
            />
          </div>

          {latestEntries.length > 0 && (
            <Card className="mb-6 max-w-5xl">
              <CardContent>
                <h3 className="mb-3 text-sm font-medium">{t('rates.latest')}</h3>
                <div className="flex flex-wrap gap-2">
                  {latestEntries.map((e) => (
                    <div
                      key={e.historyId}
                      className="rounded-md border px-3 py-2 text-sm"
                      title={new Date(e.date).toLocaleString()}
                    >
                      <span className="font-mono text-xs text-muted-foreground">
                        {e.currencyBase} → {e.currencyExchange}
                      </span>
                      <div className="font-mono">{formatRate(e.rate)}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">{t('rates.latestHint')}</p>
              </CardContent>
            </Card>
          )}

          {historyLoading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-56">{t('rates.columns.date')}</TableHead>
                      <TableHead className="w-28">{t('rates.columns.base')}</TableHead>
                      <TableHead className="w-28">{t('rates.columns.quoted')}</TableHead>
                      <TableHead>{t('rates.columns.rate')}</TableHead>
                      <TableHead className="w-28">{t('rates.columns.batch')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((e) => (
                      <TableRow key={e.historyId}>
                        <TableCell className="text-muted-foreground">
                          {new Date(e.date).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono">{e.currencyBase || e.currencyBaseId}</TableCell>
                        <TableCell className="font-mono">
                          {e.currencyExchange || e.currencyExchangeId}
                        </TableCell>
                        <TableCell className="font-mono">{formatRate(e.rate)}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {e.currencyHistoryId}
                        </TableCell>
                      </TableRow>
                    ))}
                    {entries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                          {t('rates.empty')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                total={history?.total ?? 0}
                offset={offset}
                pageSize={PAGE_SIZE}
                onOffset={setOffset}
                label={t('rates.total', { count: history?.total ?? 0 })}
              />
            </>
          )}
        </TabsPanel>
      </Tabs>
    </div>
  )
}

/**
 * formatRate trims the trailing zeros of a decimal(28,14) without ever turning
 * it into a Number: 14 decimals do not survive a float, and the value is only
 * ever displayed.
 */
function formatRate(rate: string): string {
  if (!rate.includes('.')) return rate
  const trimmed = rate.replace(/0+$/, '')
  return trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
